import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import GoogleIcon from '../../../icon/GoogleIcon'
import MuiFilterSelect from '../../../components/Filters/MuiFilterSelect'
import MuiGroupedFilterSelect from '../../../components/Filters/MuiGroupedFilterSelect'
import DatePicker from 'react-multi-date-picker'
import Toolbar from 'react-multi-date-picker/plugins/toolbar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar } from '@fortawesome/free-solid-svg-icons'

import SearchInput from '../../../components/SearchInput'
import {
  SPECIAL_PRICE_FILTER_DATE_LABEL,
  SPECIAL_PRICE_FILTER_NOTE,
  SPECIAL_PRICE_FILTER_SEARCH_PLACEHOLDER,
  SPECIAL_PRICE_FILTER_TITLE
} from './specialPriceFilterConstants'
import {
  buildSpecialPriceFilterItems,
  buildClientAttributeGroups,
  formatDateRangeLabel,
  getDateRangeFieldClassName,
  getNormalizedDateRangeChange,
  normalizeDateRangeValue
} from './specialPriceFilterHelpers'
import { DATE_PICKER_SETTINGS } from '../../../constants/constant'

const SpecialPriceFilters = ({
  clientAttributeFilter,
  productFilter,
  salesUnitFilter,
  statusFilter,
  clientAttributeOptions,
  productOptions,
  salesUnitOptions,
  statusOptions,
  searchInput,
  dateRange,
  onSearchChange,
  onSearchEnter,
  onClearSearch,
  onClientAttributeChange,
  onProductChange,
  onSalesUnitChange,
  onStatusChange,
  onDateRangeChange,
  onReset,
  shouldShowReset = false,
  isLoading = false
}) => {
  const clientAttributeGroups = useMemo(
    () => buildClientAttributeGroups(clientAttributeOptions),
    [clientAttributeOptions]
  )
  const committedDateRange = useMemo(() => normalizeDateRangeValue(dateRange), [dateRange])
  const [pendingDateRange, setPendingDateRange] = useState(committedDateRange)
  const formattedDateRangeLabel = useMemo(() => formatDateRangeLabel(pendingDateRange), [pendingDateRange])
  const filterItems = useMemo(
    () =>
      buildSpecialPriceFilterItems({
        clientAttributeFilter,
        productFilter,
        salesUnitFilter,
        statusFilter,
        clientAttributeGroups,
        productOptions,
        salesUnitOptions,
        statusOptions,
        onClientAttributeChange,
        onProductChange,
        onSalesUnitChange,
        onStatusChange
      }),
    [
      clientAttributeFilter,
      productFilter,
      salesUnitFilter,
      statusFilter,
      clientAttributeGroups,
      productOptions,
      salesUnitOptions,
      statusOptions,
      onClientAttributeChange,
      onProductChange,
      onSalesUnitChange,
      onStatusChange
    ]
  )

  const filtersDisabled = isLoading

  useEffect(() => {
    setPendingDateRange(committedDateRange)
  }, [committedDateRange])

  const handleDateRangeChange = (nextDateRange) => {
    const normalizedNextDateRange = getNormalizedDateRangeChange(nextDateRange)

    setPendingDateRange(normalizedNextDateRange)
    onDateRangeChange(normalizedNextDateRange)
  }

  return (
    <>
      <div className="promo-card-heading mb-16">
        <span className="special-price-filters__note-label">Nota: </span>
        {SPECIAL_PRICE_FILTER_NOTE}
      </div>

      <Typography variant="subtitle1" className="special-price-filters__title">
        {SPECIAL_PRICE_FILTER_TITLE}
      </Typography>

      <Box className="special-price-filters">
        <Box className="special-price-filters__group">
          <GoogleIcon className="filter-icon-poc oc-icon-primary" name="page_info" size={22} />

          <Box
            className={getDateRangeFieldClassName(pendingDateRange)}
          >
            <DatePicker
              disabled={filtersDisabled}
              range
              rangeHover
              dateSeparator=" - "
              months={DATE_PICKER_SETTINGS.months}
              weekDays={DATE_PICKER_SETTINGS.weekDays}
              format="DD MMM"
              weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
              highlightToday={false}
              numberOfMonths={2}
              onChange={handleDateRangeChange}
              value={pendingDateRange}
              plugins={[
                <Toolbar
                  key="toolbar"
                  position="bottom"
                  names={{ deselect: "Deseleccionar" }}
                  sort={["deselect"]}
                />
              ]}
              render={(_, openCalendar) => (
                <div
                  key="flr-date"
                  className={`special-price-date-trigger ${filtersDisabled ? 'special-price-date-trigger--disabled' : ''}`}
                >
                  <div className="react-filter-box filter-input-width-datepicker">
                    <div
                      title={SPECIAL_PRICE_FILTER_DATE_LABEL}
                      onClick={!filtersDisabled ? openCalendar : undefined}
                      className="react-filter-box-clickable-zone"
                    >
                      <div className="react-filter-titles">
                        {formattedDateRangeLabel ? (
                          <>
                            <div className="filter-title-overflow">{formattedDateRangeLabel}</div>
                            <FontAwesomeIcon
                              className="special-price-filters__date-icon special-price-filters__date-icon--filled"
                              icon={faCalendar}
                            />
                          </>
                        ) : (
                          <>
                            <div className="filter-title-overflow">{SPECIAL_PRICE_FILTER_DATE_LABEL}</div>
                            <FontAwesomeIcon
                              className="special-price-filters__date-icon"
                              icon={faCalendar}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            />
          </Box>

          {filterItems.map((filterConfig) => {
            return (
              <Box
                key={filterConfig.key}
                className={filterConfig.widthClassName}
              >
                {filterConfig.isGrouped ? (
                  <MuiGroupedFilterSelect
                    title={filterConfig.title}
                    groups={filterConfig.options}
                    value={filterConfig.value}
                    onChange={filterConfig.onChange}
                    disabled={filtersDisabled}
                    searchable
                    width="100%"
                    dropdownWidth={filterConfig.dropdownWidth}
                  />
                ) : (
                  <MuiFilterSelect
                    title={filterConfig.title}
                    options={filterConfig.options}
                    value={filterConfig.value}
                    onChange={filterConfig.onChange}
                    disabled={filtersDisabled}
                    searchable
                    width="100%"
                    dropdownWidth={filterConfig.dropdownWidth}
                  />
                )}
              </Box>
            )
          })}
        </Box>

        <Box
          className="special-price-filters__search"
        >
          <SearchInput
            value={searchInput}
            onChange={onSearchChange}
            onEnter={onSearchEnter}
            onClear={onClearSearch}
            placeholder={SPECIAL_PRICE_FILTER_SEARCH_PLACEHOLDER}
            disabled={filtersDisabled}
          />
        </Box>
      </Box>

      {shouldShowReset && (
        <Typography variant="body2" className="special-price-reset-filter" onClick={onReset}>
          Restablecer filtros aplicados
        </Typography>
      )}
    </>
  )
}

export default SpecialPriceFilters
